// Pure tooltip / description text for one tile. Fog tiles say only "unexplored".

import { tileKey } from './minimapGeometry.js';
import { fmt } from './minimapLabels.js';
import type { MinimapDetails, MinimapLabels, MinimapTile } from './types.js';

export interface TooltipContext {
  readonly labels: MinimapLabels;
  readonly details?: MinimapDetails;
  readonly localPlayer?: number;
}

function playerName(ctx: TooltipContext, id: number): string {
  return id === ctx.localPlayer ? ctx.labels.you : fmt(ctx.labels.player, { player: id });
}

/** Lines shown in the tooltip; the first is always the coordinates. */
export function tooltipLines(
  tile: MinimapTile | undefined,
  q: number,
  r: number,
  ctx: TooltipContext,
): string[] {
  const { labels } = ctx;
  const coords = fmt(labels.tileCoords, { q, r });
  if (!tile || tile.visibility === 'fog') return [coords, labels.tileUnexplored];
  const lines = [coords, labels.biomes[tile.biome] ?? tile.biome];
  const key = tileKey(q, r);
  // `controlledBy` is null for unclaimed land and a player id otherwise — 0 is
  // the human, so a controlled tile always names its owner (the human reads as
  // "you"). A (non-neutral) city on the tile still names its owner when the
  // tile itself is unclaimed.
  const city = tile.hasCity ? ctx.details?.cities?.get(key) : undefined;
  const ownerId =
    tile.controlledBy !== null && tile.controlledBy !== undefined
      ? tile.controlledBy
      : city && !city.neutral
        ? city.ownerId
        : undefined;
  if (ownerId !== undefined) lines.push(fmt(labels.owner, { owner: playerName(ctx, ownerId) }));
  else lines.push(labels.unclaimed);
  if (tile.hasCity) {
    const c = city;
    const head = c?.neutral
      ? labels.village
      : c?.name
        ? fmt(labels.cityNamed, { name: c.name })
        : labels.city;
    const bits = [head];
    if (c?.isCapital) bits.push(labels.capital);
    if (c?.tier !== undefined) bits.push(fmt(labels.cityTier, { tier: c.tier }));
    lines.push(bits.join(' · '));
  }
  if (tile.hasUnit) {
    const u = ctx.details?.units?.get(key);
    if (u && u.ownCount > 0) lines.push(fmt(labels.unitsOwn, { count: u.ownCount }));
    else lines.push(fmt(labels.unitsOther, { count: u?.count ?? 1 }));
  }
  return lines;
}

/** One-line description for the live region / aria. */
export function describeTile(
  tile: MinimapTile | undefined,
  q: number,
  r: number,
  ctx: TooltipContext,
): string {
  return tooltipLines(tile, q, r, ctx).slice(1).join(', ');
}
